import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { get, post, patch, del } from '@/lib/api'

// ==================== Types ====================

export interface Timelog {
  id: string
  issueId: string
  employeeId: string
  logDate: string
  minutes: number
  description: string | null
  createdAt: string
  updatedAt: string | null
  deletedAt: string | null
}

export interface TimelogWithIssue extends Timelog {
  issue?: {
    code: string
    title: string
    projectId: string
  }
}

export interface TimelogStatistics {
  totalMinutes: number
  totalHours: number
  byProject: Array<{
    projectId: string
    projectName: string
    minutes: number
    hours: number
  }>
  byEmployee: Array<{
    employeeId: string
    employeeName: string
    minutes: number
    hours: number
  }>
  byDate: Array<{
    date: string
    minutes: number
    hours: number
  }>
}

export interface TimelogExportData {
  timelogs: Array<{
    id: string
    issueId: string
    issueCode: string
    issueTitle: string
    employeeId: string
    logDate: string
    minutes: number
    hours: number
    description: string | null
    createdAt: string
  }>
  summary: {
    totalMinutes: number
    totalHours: number
    recordCount: number
  }
}

export interface TimelogListParams {
  issueId?: string
  employeeId?: string
  projectId?: string
  startDate?: string
  endDate?: string
  page?: number
  pageSize?: number
  sortBy?: 'logDate' | 'createdAt' | 'minutes'
  sortOrder?: 'asc' | 'desc'
}

export interface TimelogStatsParams {
  projectId?: string
  employeeId?: string
  startDate?: string
  endDate?: string
  groupBy?: 'project' | 'employee' | 'date'
}

export interface TimelogExportParams {
  issueId?: string
  employeeId?: string
  startDate?: string
  endDate?: string
}

export interface CreateTimelogInput {
  issueId: string
  logDate: string
  minutes: number
  description?: string
}

export interface UpdateTimelogInput {
  logDate?: string
  minutes?: number
  description?: string
}

// ==================== Query Keys ====================

export const timelogKeys = {
  all: ['timelogs'] as const,
  lists: () => [...timelogKeys.all, 'list'] as const,
  list: (params: TimelogListParams) => [...timelogKeys.lists(), params] as const,
  details: () => [...timelogKeys.all, 'detail'] as const,
  detail: (id: string) => [...timelogKeys.details(), id] as const,
  stats: (params: TimelogStatsParams) => [...timelogKeys.all, 'stats', params] as const,
  export: (params: TimelogExportParams) => [...timelogKeys.all, 'export', params] as const,
}

// ==================== Hooks ====================

/**
 * 获取工时列表
 */
export function useTimelogs(params: TimelogListParams = {}) {
  return useQuery({
    queryKey: timelogKeys.list(params),
    queryFn: () => get<TimelogWithIssue[]>('/timelogs', params as Record<string, unknown>),
  })
}

/**
 * 获取单个工时记录
 */
export function useTimelog(id: string) {
  return useQuery({
    queryKey: timelogKeys.detail(id),
    queryFn: () => get<Timelog>(`/timelogs/${id}`),
    enabled: !!id,
  })
}

/**
 * 创建工时记录
 */
export function useCreateTimelog() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateTimelogInput) => post<Timelog>('/timelogs', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: timelogKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: ['issues'] })
    },
  })
}

/**
 * 更新工时记录
 */
export function useUpdateTimelog() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTimelogInput }) =>
      patch<Timelog>(`/timelogs/${id}`, data),
    onSuccess: (timelog) => {
      void queryClient.invalidateQueries({ queryKey: timelogKeys.detail(timelog.id) })
      void queryClient.invalidateQueries({ queryKey: timelogKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: ['issues'] })
    },
  })
}

/**
 * 删除工时记录
 */
export function useDeleteTimelog() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => del<null>(`/timelogs/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: timelogKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: ['issues'] })
    },
  })
}

/**
 * 获取工时统计
 */
export function useTimelogStats(params: TimelogStatsParams = {}) {
  return useQuery({
    queryKey: timelogKeys.stats(params),
    queryFn: () => get<TimelogStatistics>('/timelogs/stats', params as Record<string, unknown>),
  })
}

/**
 * 获取工时导出数据
 */
export function useTimelogExport(params: TimelogExportParams = {}) {
  return useQuery({
    queryKey: timelogKeys.export(params),
    queryFn: () => get<TimelogExportData>('/timelogs/export', params as Record<string, unknown>),
    enabled: false, // 手动触发
  })
}

